import type {
	CoreDetails,
	CoreRequest,
	NativeCoreClass,
	NativeCoreRequest,
	StoredCoreClass,
	UncertainCoreRequest,
} from "@signaloid/cloud-compute-engine-client";
import type { ObjectValues } from "@/types/utilities";

export type CoreSpecs = {
	Class: string;
	Precision: number;
	MemorySize: number;
	Microarchitecture: string;
	DependencyTracking?: string;
	CorrelationTracking?: string;
};

export type CoreID = string;
export type Core = Omit<CoreDetails, "Microarchitecture"> & {
	Microarchitecture: CoreMicroarchitecture;
	Immutable: boolean;
	Disabled: boolean;
	Hidden: boolean;
	Deprecated: boolean;
};

export type CreateCoreConfig = Pick<Core, "Name" | "Class" | "Precision" | "Microarchitecture" | "DependencyTracking">;

export const CoreDependencyTrackingE = {
	None: "None",
	Basic: "Basic",
	Advanced: "Advanced",
} as const;
export type CoreDependencyTracking = ObjectValues<typeof CoreDependencyTrackingE>;

export const CoreClassE = {
	C0: "C0",
	C0Pro: "C0Pro",
	C0SD: "C0-SD",
	C0MicroSD: "C0-microSD",
	C0MicroSDPlus: "C0-microSD-plus",
	R7i: "r7i",
} as const satisfies Record<string, StoredCoreClass>;
export type CoreClass = (typeof CoreClassE)[keyof typeof CoreClassE];

export const SERVED_CORE_CLASSES: CoreClass[] = [
	CoreClassE.C0Pro,
	CoreClassE.C0SD,
	CoreClassE.C0MicroSD,
	CoreClassE.C0MicroSDPlus,
	CoreClassE.R7i,
];

export function isServedCoreClass(value: unknown): value is CoreClass {
	return SERVED_CORE_CLASSES.some((servedClass) => servedClass === value);
}

export const COMPUTE_MODULE_CORE_CLASSES: CoreClass[] = [
	CoreClassE.C0SD,
	CoreClassE.C0MicroSD,
	CoreClassE.C0MicroSDPlus,
];

export function isComputeModuleCoreClass(coreClass: string | undefined): boolean {
	return COMPUTE_MODULE_CORE_CLASSES.some((computeModuleClass) => computeModuleClass === coreClass);
}

/** Core classes that run without uncertainty tracking. */
export const NATIVE_CORE_CLASSES = [CoreClassE.R7i] as const satisfies readonly NativeCoreClass[];

export function isNativeCoreClass(coreClass: string | undefined): coreClass is NativeCoreClass {
	return NATIVE_CORE_CLASSES.some((nativeClass) => nativeClass === coreClass);
}

export function coreClassDisplayName(coreClass: string): string {
	return isNativeCoreClass(coreClass) ? "Native" : coreClass;
}

/**
 * Builds the SDK create/update payload for a Core. Native Cores take a `NativeCoreRequest`, which
 * leaves out `Precision` and `DependencyTracking` so the API fills them in. Every other class
 * takes an `UncertainCoreRequest`.
 */
export function coreRequestFromConfig(config: CreateCoreConfig): CoreRequest {
	if (isNativeCoreClass(config.Class)) {
		return {
			Name: config.Name,
			Class: config.Class,
			Microarchitecture: config.Microarchitecture as NativeCoreRequest["Microarchitecture"],
		};
	}
	return {
		Name: config.Name,
		Class: config.Class as UncertainCoreRequest["Class"],
		Precision: config.Microarchitecture === CoreMicroarchitectureE.Bypass ? 0 : config.Precision!,
		Microarchitecture: config.Microarchitecture as UncertainCoreRequest["Microarchitecture"],
		DependencyTracking: config.DependencyTracking!,
	};
}

export const CoreMicroarchitectureE = {
	Athens: "Athens",
	Rome: "Rome",
	Mars: "Mars",
	Saturn: "Saturn",
	Reference: "Reference",
	Jupiter: "Jupiter",
	Bypass: "Bypass",
	// TODO: verify against the native Monte Carlo core once it ships.
	Native: "Native",
} as const;
export type CoreMicroarchitecture = (typeof CoreMicroarchitectureE)[keyof typeof CoreMicroarchitectureE];

export type CoreMicroarchitectureObject = {
	ID: number;
	Name: CoreMicroarchitecture;
	Support: {
		DependencyTracking: CoreDependencyTracking[];
		CustomPrecision: boolean;
		DistributionTracking: boolean;
	};
	Disabled: boolean;
	Experimental: boolean;
	Restricted: boolean;
};

export type CoreClassObject = {
	Name: CoreClass;
	Enabled: boolean;
	Creatable: boolean;
	Experimental?: boolean;
	SupportedMicroarchitectures: string[];
};

export type getUserCoresResponse = {
	UserID: string;
	Cores: Core[];
	Count: number;
};

export type CoreRequirementMatch = {
	[Property in keyof Core]: boolean;
};
